import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { v4 as uuidv4 } from 'uuid';
import type {
  PreparedSupportDiagnostic,
  SupportDiagnosticConsent,
  SupportDiagnosticPreparedStore,
  SupportDiagnosticTransport,
  SupportDiagnosticUploadReceipt,
} from '@/types/supportDiagnostic';
import {
  SUPPORT_DIAGNOSTIC_MAX_BYTES,
  SupportDiagnosticBuffer,
} from './SupportDiagnosticBuffer';

interface SupportDiagnosticServiceDependencies {
  buffer: SupportDiagnosticBuffer;
  transport: SupportDiagnosticTransport;
  store?: SupportDiagnosticPreparedStore;
  now?: () => Date;
  createId?: () => string;
}

interface ExplicitSendAuthorization {
  explicitUserAction: true;
  consent: SupportDiagnosticConsent;
}

interface AdministrativeSendAuthorization {
  administrativeRequestId: string;
}

class InMemoryPreparedStore implements SupportDiagnosticPreparedStore {
  private readonly entries = new Map<string, PreparedSupportDiagnostic>();

  async save(prepared: PreparedSupportDiagnostic): Promise<void> {
    this.entries.set(prepared.diagnosticId, clonePrepared(prepared));
  }

  async load(diagnosticId: string): Promise<PreparedSupportDiagnostic | null> {
    const prepared = this.entries.get(diagnosticId);
    return prepared ? clonePrepared(prepared) : null;
  }

  async loadLatest(): Promise<PreparedSupportDiagnostic | null> {
    const prepared = Array.from(this.entries.values()).at(-1);
    return prepared ? clonePrepared(prepared) : null;
  }

  async remove(diagnosticId: string): Promise<void> {
    this.entries.delete(diagnosticId);
  }
}

const clonePrepared = (prepared: PreparedSupportDiagnostic): PreparedSupportDiagnostic => ({
  ...prepared,
  body: new Uint8Array(prepared.body),
});

const validateConsent = (consent: SupportDiagnosticConsent | null | undefined): void => {
  if (
    !consent ||
    consent.granted !== true ||
    consent.purpose !== 'support_diagnostic' ||
    !consent.grantedAt ||
    Number.isNaN(Date.parse(consent.grantedAt))
  ) {
    throw new Error('Consentimento explicito para diagnostico ausente ou invalido.');
  }
};

export class SupportDiagnosticService {
  private readonly buffer: SupportDiagnosticBuffer;
  private readonly transport: SupportDiagnosticTransport;
  private readonly store: SupportDiagnosticPreparedStore;
  private readonly now: () => Date;
  private readonly createId: () => string;

  constructor(dependencies: SupportDiagnosticServiceDependencies) {
    this.buffer = dependencies.buffer;
    this.transport = dependencies.transport;
    this.store = dependencies.store ?? new InMemoryPreparedStore();
    this.now = dependencies.now ?? (() => new Date());
    this.createId = dependencies.createId ?? uuidv4;
  }

  async prepareForExplicitSend(input: {
    consent: SupportDiagnosticConsent;
    metadata?: unknown;
  }): Promise<PreparedSupportDiagnostic> {
    validateConsent(input.consent);
    return this.prepare({
      authorization: {
        kind: 'explicit_user_action',
        consent: input.consent,
      },
      metadata: input.metadata,
    });
  }

  async prepareForAdministrativeRequest(input: {
    requestId: string;
    metadata?: unknown;
  }): Promise<PreparedSupportDiagnostic> {
    if (!input.requestId.trim()) {
      throw new Error('Solicitação administrativa de diagnóstico inválida.');
    }
    return this.prepare({
      authorization: {
        kind: 'administrative_request',
      },
      administrativeRequestId: input.requestId,
      metadata: input.metadata,
    });
  }

  async prepareForSystemAutomatic(input: { metadata?: unknown }): Promise<PreparedSupportDiagnostic> {
    return this.prepare({ authorization: { kind: 'system_automatic' }, metadata: input.metadata });
  }

  async sendSystemAutomatic(diagnosticId: string): Promise<SupportDiagnosticUploadReceipt> {
    const prepared = await this.store.load(diagnosticId);
    if ((prepared?.snapshot.metadata as any)?.delivery_basis?.kind !== 'system_automatic') {
      throw new Error('Origem automatica diverge do diagnostico preparado.');
    }
    return this.sendPrepared(diagnosticId);
  }

  private async prepare(input: {
    authorization: unknown;
    administrativeRequestId?: string;
    metadata?: unknown;
  }): Promise<PreparedSupportDiagnostic> {
    const diagnosticId = this.createId();
    const createdAt = this.now().toISOString();
    let snapshot = this.buffer.createImmutableSnapshot({
      diagnosticId,
      createdAt,
      metadata: {
        delivery_basis: input.authorization,
        context: input.metadata ?? null,
      },
    });
    let body = naclUtil.decodeUTF8(JSON.stringify(snapshot));
    if (body.length > SUPPORT_DIAGNOSTIC_MAX_BYTES) {
      const events = [...snapshot.events];
      const initialDroppedByByteLimit = snapshot.stats.droppedByByteLimit;
      let removed = 0;
      while (body.length > SUPPORT_DIAGNOSTIC_MAX_BYTES && events.length > 0) {
        const removeCount = Math.max(1, Math.ceil(events.length / 4));
        events.splice(0, removeCount);
        removed += removeCount;
        snapshot = freezeSnapshot({
          ...snapshot,
          events,
          stats: {
            ...snapshot.stats,
            eventCount: events.length,
            byteLength: naclUtil.decodeUTF8(JSON.stringify(events)).length,
            droppedByByteLimit: initialDroppedByByteLimit + removed,
          },
        });
        body = naclUtil.decodeUTF8(JSON.stringify(snapshot));
      }
      if (body.length > SUPPORT_DIAGNOSTIC_MAX_BYTES) {
        throw new Error('Metadados do diagnóstico excedem o limite de 10 MiB.');
      }
    }
    const sha512 = naclUtil.encodeBase64(nacl.hash(body));
    const prepared: PreparedSupportDiagnostic = {
      diagnosticId,
      administrativeRequestId: input.administrativeRequestId,
      createdAt,
      byteLength: body.length,
      sha512,
      contentType: 'application/vnd.app-guincho.support-diagnostic+json',
      idempotencyKey: `support-diagnostic:${diagnosticId}:${sha512}`,
      body,
      snapshot,
    };
    await this.store.save(prepared);
    return clonePrepared(prepared);
  }

  async sendExplicit(
    diagnosticId: string,
    authorization: ExplicitSendAuthorization
  ): Promise<SupportDiagnosticUploadReceipt> {
    if (authorization.explicitUserAction !== true) {
      throw new Error('Envio de diagnostico exige acao explicita do usuario.');
    }
    validateConsent(authorization.consent);
    return this.sendPrepared(diagnosticId);
  }

  async sendAdministrative(
    diagnosticId: string,
    authorization: AdministrativeSendAuthorization
  ): Promise<SupportDiagnosticUploadReceipt> {
    if (!authorization.administrativeRequestId.trim()) {
      throw new Error('Solicitação administrativa de diagnóstico inválida.');
    }
    const prepared = await this.store.load(diagnosticId);
    if (
      prepared?.administrativeRequestId !== authorization.administrativeRequestId ||
      (prepared.snapshot.metadata as any)?.delivery_basis?.kind !== 'administrative_request'
    ) {
      throw new Error('Solicitação administrativa diverge do diagnóstico preparado.');
    }
    return this.sendPrepared(diagnosticId);
  }

  private async sendPrepared(diagnosticId: string): Promise<SupportDiagnosticUploadReceipt> {
    const prepared = await this.store.load(diagnosticId);
    if (!prepared) {
      throw new Error('Diagnostico preparado nao encontrado.');
    }

    const receipt = await this.transport.uploadExplicit({
      diagnosticId: prepared.diagnosticId,
      byteLength: prepared.byteLength,
      sha512: prepared.sha512,
      contentType: prepared.contentType,
      idempotencyKey: prepared.idempotencyKey,
      body: new Uint8Array(prepared.body),
    });
    if (
      receipt.diagnosticId !== prepared.diagnosticId ||
      receipt.sha512 !== prepared.sha512 ||
      receipt.byteLength !== prepared.byteLength ||
      !receipt.protocol.trim() ||
      !receipt.remotePath.trim()
    ) {
      throw new Error('Recibo do diagnostico diverge do pacote enviado.');
    }
    return receipt;
  }

  async loadLatestPrepared(): Promise<PreparedSupportDiagnostic | null> {
    const prepared = await this.store.loadLatest();
    return prepared ? clonePrepared(prepared) : null;
  }

  async discardPrepared(diagnosticId: string): Promise<void> {
    await this.store.remove(diagnosticId);
  }
}

const freezeSnapshot = <T>(value: T): T => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value as Record<string, unknown>).forEach(freezeSnapshot);
  }
  return value;
};
